import type { AppNotification } from '@/types/notifications';
import { NOTIFICATION_CATALOG } from '@/data/notificationCatalog';
import { formatRelativeTime } from '@/lib/formatRelativeTime';
import { cn } from '@/lib/utils';
import i18n from '@/i18n';

type NotificationListProps = {
  notifications: AppNotification[];
  onItemClick: (id: string, contactId?: string | null) => void;
  emptyMessage?: string;
  className?: string;
};

export function NotificationList({
  notifications,
  onItemClick,
  emptyMessage = i18n.getFixedT(null, 'notifications')('empty'),
  className,
}: NotificationListProps) {
  if (notifications.length === 0) {
    return (
      <div className={cn('px-4 py-10 text-center text-sm text-[#64748B] dark:text-[#A1A1AA]', className)}>
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={cn('divide-y divide-[#E5E7EB] dark:divide-[#3F3F464D]', className)}>
      {notifications.map((notification) => {
        const catalog =
          NOTIFICATION_CATALOG[notification.eventType] ??
          NOTIFICATION_CATALOG['conversation.opened'];
        const Icon = catalog.icon;
        const isUnread = notification.readAt === null;

        return (
          <button
            key={notification.id}
            type="button"
            onClick={() => onItemClick(notification.id, notification.contactId)}
            className={cn(
              'flex w-full items-start gap-3 px-4 py-3.5 text-left transition-colors duration-200',
              isUnread
                ? 'bg-[#F5F8FF] hover:bg-[#EEF4FF] dark:bg-blue-500/10 dark:hover:bg-blue-500/15'
                : 'bg-transparent hover:bg-[#F8FAFC] dark:hover:bg-[#3F3F46]/40'
            )}
          >
            <div
              className={cn(
                'mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white',
                catalog.chipClassName
              )}
            >
              <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
                  {notification.title}
                </p>
                <span className="shrink-0 text-xs text-[#94A3B8] dark:text-[#71717A]">
                  {formatRelativeTime(notification.createdAt)}
                </span>
              </div>
              <p className="mt-0.5 text-sm leading-snug text-[#64748B] dark:text-[#A1A1AA]">
                {notification.body}
              </p>
            </div>

            <span
              className={cn(
                'mt-2 h-2 w-2 shrink-0 rounded-full',
                isUnread ? 'bg-[#2563EB]' : 'bg-[#CBD5E1] dark:bg-[#52525B]'
              )}
              aria-hidden
            />
          </button>
        );
      })}
    </div>
  );
}

export default NotificationList;
